// src/pages/OrderTrackingPage.js - Real-time Order Tracking System
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';

const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const [orderData, setOrderData] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(45);
  const [driverInfo, setDriverInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const lastStatusRef = useRef();

  // Order status steps
  const orderSteps = [
    {
      id: 0,
      title: "Order Received",
      description: "We've received your order and it's being reviewed",
      icon: "📋",
      time: "0 min",
      color: "#22c55e"
    },
    {
      id: 1,
      title: "Order Confirmed",
      description: "Your order has been confirmed and sent to the kitchen",
      icon: "✅",
      time: "2-5 min",
      color: "#22c55e"
    },
    {
      id: 2,
      title: "Preparing Food",
      description: "Our chefs are preparing your delicious Nigerian meal",
      icon: "👨‍🍳",
      time: "15-25 min",
      color: "#f59e0b"
    },
    {
      id: 3,
      title: "Food Ready",
      description: "Your order is ready and waiting for pickup",
      icon: "🍽️",
      time: "30-35 min",
      color: "#f59e0b"
    },
    {
      id: 4,
      title: "Out for Delivery",
      description: "Your order is on its way to you",
      icon: "🚚",
      time: "35-45 min",
      color: "#3b82f6"
    },
    {
      id: 5,
      title: "Delivered",
      description: "Enjoy your delicious Nigerian meal!",
      icon: "🎉",
      time: "45 min",
      color: "#22c55e"
    }
  ];

  // Sample order data (in real app, this would come from API)
  useEffect(() => {
    const loadOrderData = async () => {
      setLoading(true);
      try {
        // Replace with your real API endpoint
        const res = await fetch(`${process.env.REACT_APP_API_URL}/orders/${orderId}`);
        if (!res.ok) throw new Error('Order not found');
        const data = await res.json();
        setOrderData(data);
        setDriverInfo(data.driver || null);
      } catch (err) {
        setOrderData(null);
      }
      setLoading(false);
    };
    loadOrderData();
  }, [orderId]); // Added orderId to dependency array

  // Simulate real-time status updates
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - (orderData?.orderTime?.getTime() || Date.now());
      const minutesElapsed = Math.floor(elapsed / 60000);
      
      if (minutesElapsed < 5) setCurrentStatus(1);
      else if (minutesElapsed < 20) setCurrentStatus(2);
      else if (minutesElapsed < 30) setCurrentStatus(3);
      else if (minutesElapsed < 45) setCurrentStatus(4);
      else setCurrentStatus(5);
      
      // Update estimated time
      const remaining = Math.max(0, 45 - minutesElapsed);
      setEstimatedTime(remaining);
    }, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, [orderData]);

  // Toast notification for status updates
  useEffect(() => {
    if (!orderData) return;
    if (lastStatusRef.current && orderData.status !== lastStatusRef.current) {
      // Show a toast when status changes
      toast.info(`Order status updated: ${orderData.status.replace(/_/g, ' ')}`);
    }
    lastStatusRef.current = orderData.status;
  }, [orderData?.status]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))'
      }}>
        <div style={{
          background: 'white',
          borderRadius: '25px',
          padding: '3rem',
          textAlign: 'center',
          boxShadow: '0 25px 50px rgba(0,0,0,0.2)'
        }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: '1rem',
            animation: 'pulse 1.5s infinite'
          }}>
            🍽️
          </div>
          <h2 style={{ color: 'var(--nigeria-green)', marginBottom: '1rem' }}>
            Loading Your Order...
          </h2>
          <p style={{ color: 'var(--text-light)' }}>
            Fetching the latest updates on your delicious Nigerian meal
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="order-tracking-page" style={{ background: '#f8f9fa', minHeight: '100vh', paddingTop: '2rem' }}>
      <div className="container">
        {/* Header Section */}
        <div style={{
          background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))',
          borderRadius: '25px',
          padding: '3rem',
          color: 'white',
          textAlign: 'center',
          marginBottom: '3rem',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background Animation */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px)',
            backgroundSize: '40px 40px',
            animation: 'drift 15s linear infinite'
          }}></div>

          <div style={{ position: 'relative', zIndex: 2 }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '1rem',
              animation: 'bounce 2s infinite'
            }}>
              🚚
            </div>
            
            <h1 style={{ 
              fontSize: '3rem',
              marginBottom: '1rem',
              fontWeight: '900',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
            }}>
              Track Your Order
            </h1>
            
            <p style={{ 
              fontSize: '1.3rem',
              marginBottom: '2rem',
              opacity: 0.9
            }}>
              Order #{orderData?.id} • Estimated delivery in {estimatedTime} minutes
            </p>

            {/* Live Status Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(255,255,255,0.2)',
              padding: '1rem 2rem',
              borderRadius: '50px',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: orderSteps[currentStatus]?.color || '#22c55e',
                animation: 'pulse 2s infinite'
              }}></div>
              <span style={{ fontWeight: '700', fontSize: '1.1rem' }}>
                {orderSteps[currentStatus]?.title}
              </span>
            </div>
          </div>
        </div>

        <div className="order-tracking-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '3rem',
          marginBottom: '3rem'
        }}>
          {/* Order Progress */}
          <div className="order-progress-section" style={{
            background: 'white',
            borderRadius: '25px',
            padding: '3rem',
            boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
            border: '2px solid #f0f0f0'
          }}>
            <h2 style={{ 
              fontSize: '2rem',
              color: 'var(--nigeria-green)',
              marginBottom: '2rem',
              fontWeight: '800',
              textAlign: 'center'
            }}>
              📊 Order Progress
            </h2>

            <div style={{ position: 'relative' }}>
              {/* Progress Line */}
              <div style={{
                position: 'absolute',
                left: '30px',
                top: '20px',
                bottom: '20px',
                width: '4px',
                background: '#e5e7eb',
                borderRadius: '2px'
              }}>
                <div style={{
                  width: '100%',
                  height: `${(currentStatus / (orderSteps.length - 1)) * 100}%`,
                  background: 'linear-gradient(to bottom, var(--nigeria-green), var(--warm-orange))',
                  borderRadius: '2px',
                  transition: 'height 1s ease'
                }}></div>
              </div>

              {/* Progress Steps */}
              {orderSteps.map((step, index) => (
                <div key={step.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  marginBottom: '2.5rem',
                  position: 'relative',
                  zIndex: 2
                }}>
                  {/* Step Icon */}
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: index <= currentStatus 
                      ? `linear-gradient(135deg, ${step.color}, ${step.color}dd)` 
                      : '#e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    color: 'white',
                    fontWeight: '700',
                    boxShadow: index <= currentStatus ? '0 8px 20px rgba(0,0,0,0.2)' : 'none',
                    transition: 'all 0.5s ease',
                    transform: index === currentStatus ? 'scale(1.1)' : 'scale(1)',
                    animation: index === currentStatus ? 'pulse 2s infinite' : 'none'
                  }}>
                    {step.icon}
                  </div>

                  {/* Step Content */}
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      fontSize: '1.3rem',
                      fontWeight: '700',
                      color: index <= currentStatus ? 'var(--nigeria-green)' : 'var(--text-light)',
                      marginBottom: '0.5rem',
                      transition: 'color 0.3s ease'
                    }}>
                      {step.title}
                    </h3>
                    <p style={{ 
                      fontSize: '1rem',
                      color: index <= currentStatus ? 'var(--text-dark)' : 'var(--text-light)',
                      marginBottom: '0.3rem',
                      transition: 'color 0.3s ease'
                    }}>
                      {step.description}
                    </p>
                    <span style={{ 
                      fontSize: '0.9rem',
                      color: step.color,
                      fontWeight: '600'
                    }}>
                      ⏱️ {step.time}
                    </span>
                  </div>

                  {/* Active Indicator */}
                  {index === currentStatus && (
                    <div style={{
                      padding: '0.5rem 1rem',
                      background: 'rgba(34, 197, 94, 0.1)',
                      borderRadius: '15px',
                      border: '2px solid #22c55e',
                      fontSize: '0.8rem',
                      fontWeight: '700',
                      color: '#15803d',
                      animation: 'pulse 2s infinite'
                    }}>
                      ACTIVE
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Order Details & Driver Info */}
          <div>
            {/* Order Summary */}
            <div style={{
              background: 'white',
              borderRadius: '25px',
              padding: '2.5rem',
              boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
              border: '2px solid #f0f0f0',
              marginBottom: '2rem'
            }}>
              <h3 style={{ 
                fontSize: '1.8rem',
                color: 'var(--nigeria-green)',
                marginBottom: '2rem',
                fontWeight: '700',
                textAlign: 'center'
              }}>
                📋 Order Summary
              </h3>

              <div style={{ marginBottom: '2rem' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '1rem',
                  paddingBottom: '1rem',
                  borderBottom: '2px solid #f0f0f0'
                }}>
                  <span style={{ fontWeight: '600', color: 'var(--text-dark)' }}>Customer:</span>
                  <span style={{ color: 'var(--text-dark)' }}>{orderData?.customerName}</span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '1rem',
                  paddingBottom: '1rem',
                  borderBottom: '2px solid #f0f0f0'
                }}>
                  <span style={{ fontWeight: '600', color: 'var(--text-dark)' }}>Phone:</span>
                  <a href={`tel:${orderData?.phone}`} style={{ 
                    color: 'var(--nigeria-green)', 
                    textDecoration: 'none',
                    fontWeight: '600'
                  }}>
                    {orderData?.phone}
                  </a>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '1rem'
                }}>
                  <span style={{ fontWeight: '600', color: 'var(--text-dark)' }}>Address:</span>
                  <span style={{ color: 'var(--text-dark)', textAlign: 'right', maxWidth: '60%' }}>
                    {orderData?.deliveryAddress}
                  </span>
                </div>
              </div>

              <h4 style={{ 
                fontSize: '1.3rem',
                color: 'var(--nigeria-green)',
                marginBottom: '1rem',
                fontWeight: '700'
              }}>
                🍽️ Items Ordered:
              </h4>
              
              {orderData?.items.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1rem',
                  background: '#f8f9fa',
                  borderRadius: '15px',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <span style={{ 
                      fontWeight: '600',
                      color: 'var(--text-dark)',
                      fontSize: '1rem'
                    }}>
                      {item.name}
                    </span>
                    <span style={{ 
                      color: 'var(--text-light)',
                      fontSize: '0.9rem',
                      marginLeft: '0.5rem'
                    }}>
                      x{item.quantity}
                    </span>
                  </div>
                  <span style={{ 
                    fontWeight: '700',
                    color: 'var(--nigeria-green)',
                    fontSize: '1rem'
                  }}>
                    ₦{item.price.toLocaleString()}
                  </span>
                </div>
              ))}

              <div style={{
                marginTop: '1.5rem',
                paddingTop: '1.5rem',
                borderTop: '3px solid var(--nigeria-green)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span style={{ 
                  fontSize: '1.3rem',
                  fontWeight: '800',
                  color: 'var(--text-dark)'
                }}>
                  Total:
                </span>
                <span style={{ 
                  fontSize: '1.5rem',
                  fontWeight: '900',
                  color: 'var(--nigeria-green)'
                }}>
                  ₦{orderData?.total.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Driver Information (shown when out for delivery) */}
            {currentStatus >= 4 && driverInfo && (
              <div style={{
                background: 'linear-gradient(135deg, var(--nigeria-green), var(--warm-orange))',
                borderRadius: '25px',
                padding: '2.5rem',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: 0.1,
                  backgroundImage: 'radial-gradient(circle at 30% 70%, white 2px, transparent 2px)',
                  backgroundSize: '30px 30px',
                  animation: 'drift 10s linear infinite'
                }}></div>

                <div style={{ position: 'relative', zIndex: 2 }}>
                  <h3 style={{ 
                    fontSize: '1.8rem',
                    marginBottom: '2rem',
                    fontWeight: '700',
                    textAlign: 'center',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                  }}>
                    🚚 Your Delivery Driver
                  </h3>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2rem',
                    marginBottom: '2rem'
                  }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '3rem',
                      border: '3px solid rgba(255,255,255,0.3)',
                      backdropFilter: 'blur(10px)'
                    }}>
                      {driverInfo.photo}
                    </div>
                    
                    <div>
                      <h4 style={{ 
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        marginBottom: '0.5rem',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
                      }}>
                        {driverInfo.name}
                      </h4>
                      <p style={{ 
                        fontSize: '1rem',
                        opacity: 0.9,
                        marginBottom: '0.5rem'
                      }}>
                        🏍️ {driverInfo.vehicle}
                      </p>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span>⭐</span>
                        <span style={{ fontWeight: '600' }}>
                          {driverInfo.rating}/5.0 Rating
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'center'
                  }}>
                    <a
                      href={`tel:${driverInfo.phone}`}
                      style={{
                        background: 'rgba(255,255,255,0.9)',
                        color: 'var(--nigeria-green)',
                        padding: '12px 20px',
                        borderRadius: '20px',
                        textDecoration: 'none',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = 'white';
                        e.target.style.transform = 'translateY(-2px)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = 'rgba(255,255,255,0.9)';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      📞 Call Driver
                    </a>
                    
                    <button
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '2px solid rgba(255,255,255,0.8)',
                        padding: '12px 20px',
                        borderRadius: '20px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = 'rgba(255,255,255,0.3)';
                        e.target.style.transform = 'translateY(-2px)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = 'rgba(255,255,255,0.2)';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      💬 Message
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Order Timeline */}
        <div style={{
          background: 'white',
          borderRadius: '25px',
          padding: '3rem',
          boxShadow: '0 15px 40px rgba(0,0,0,0.1)',
          border: '2px solid #f0f0f0',
          textAlign: 'center'
        }}>
          <h2 style={{ 
            fontSize: '2rem',
            color: 'var(--nigeria-green)',
            marginBottom: '2rem',
            fontWeight: '800'
          }}>
            ⏰ Estimated Timeline
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem'
          }}>
            <div style={{
              padding: '2rem',
              background: '#f8f9fa',
              borderRadius: '20px',
              border: '2px solid #e9ecef'
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📅</div>
              <h4 style={{ 
                color: 'var(--nigeria-green)', 
                marginBottom: '0.5rem',
                fontWeight: '700'
              }}>
                Order Placed
              </h4>
              <p style={{ color: 'var(--text-dark)', margin: 0 }}>
                {orderData?.orderTime?.toLocaleTimeString()}
              </p>
            </div>
            
            <div style={{
              padding: '2rem',
              background: currentStatus >= 4 ? 'rgba(34, 197, 94, 0.1)' : '#f8f9fa',
              borderRadius: '20px',
              border: `2px solid ${currentStatus >= 4 ? '#22c55e' : '#e9ecef'}`
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🚚</div>
              <h4 style={{ 
                color: 'var(--nigeria-green)', 
                marginBottom: '0.5rem',
                fontWeight: '700'
              }}>
                Out for Delivery
              </h4>
              <p style={{ color: 'var(--text-dark)', margin: 0 }}>
                {currentStatus >= 4 ? 'Now' : 'In progress...'}
              </p>
            </div>
            
            <div style={{
              padding: '2rem',
              background: currentStatus >= 5 ? 'rgba(34, 197, 94, 0.1)' : '#f8f9fa',
              borderRadius: '20px',
              border: `2px solid ${currentStatus >= 5 ? '#22c55e' : '#e9ecef'}`
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🎉</div>
              <h4 style={{ 
                color: 'var(--nigeria-green)', 
                marginBottom: '0.5rem',
                fontWeight: '700'
              }}>
                Delivered
              </h4>
              <p style={{ color: 'var(--text-dark)', margin: 0 }}>
                {currentStatus >= 5 ? 'Completed!' : orderData?.estimatedDelivery?.toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', margin: '3rem 0 1rem 0' }}>
          <Link to="/orders" style={{
            background: '#008751',
            color: 'white',
            padding: '0.9rem 2.2rem',
            borderRadius: '20px',
            textDecoration: 'none',
            fontWeight: '800',
            fontSize: '1.1rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.07)'
          }}>
            ← Back to Orders
          </Link>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }
        
        @keyframes drift {
          0% { transform: translateX(0); }
          100% { transform: translateX(20px); }
        }

        @media (max-width: 700px) {
          .order-progress-section {
            padding-right: 0.5rem !important;
            padding-left: 0.7rem !important;
            padding-top: 1.2rem !important;
            padding-bottom: 1.2rem !important;
          }
          .order-tracking-grid {
            grid-template-columns: 1fr !important;
            gap: 1.2rem !important;
          }
        }
      `}</style>
    </div>
  );
};


export default OrderTrackingPage;